import { useIsVitrine } from '@/lib/use-instance';
import { cn } from '@/lib/utils';
import { VERSION_TAG_LABEL } from '@/lib/versioned-labels';
import type { VersionTag } from '@matter_extractor/shared';

/**
 * Where a pointer sits: « production v3 », « développement v5 » (refonte
 * modulaire §4).
 *
 * ## The colours are a meaning and not a decoration
 *
 * `vert` for production and `bleu-ciel` for development, at their `-tint` /
 * `-deep` pair so the ink clears the contrast floor on the ground the pill
 * paints. The charte holds this app at level 2 - black, white and `bleu-nuit` -
 * and authorises a noyau where it CARRIES something: here it carries which of
 * the two pointers an operator is looking at, on a screen where every card
 * shows both. Two grey pills would be a pair the eye has to read word by word.
 *
 * ## The production pill is ABSENT and never « v- »
 *
 * §4 is explicit: the pill is left out when nothing has ever been passed to
 * production, « pour que l'œil trouve tout de suite ce qui ne l'a jamais été ».
 * That is why the caller renders `null` rather than this component with a
 * placeholder - a « production - » would be a pill the eye counts and then has
 * to decode, which is the exact opposite of what the absence buys.
 *
 * `version` is optional because the same pill says two things: WHICH version a
 * pointer is on (on a card, in the editor's head) and THAT a pointer is on this
 * line (in the history, where the line already carries its own number).
 *
 * ## On the vitrine it is the plain « v12 », once
 *
 * The vitrine has ONE state (docs/specs/vitrine.md section 7, issue #192), so
 * there is no pointer to tell apart: the production pill says the version the
 * vitrine plays as plain text, and the development pill - which would say the
 * same number again - is not drawn. A pill with no number, which only said THAT
 * a pointer sits on a history line, is not drawn either. Every consumer of this
 * component (the pickers, the chooser, the popovers, the head of a session)
 * gets it without a line of its own.
 *
 * `entity` is the `data-testid` prefix of whatever object is being shown -
 * `critere` gives `critere-pastille-production`. It is a prop and not a
 * constant because this pill is the one component of the layer that EVERY
 * screen of every versioned object mounts, so a hardcoded mark would be the
 * one place a persona list still said « critere ».
 */

const TAG_CLASS: Readonly<Record<VersionTag, string>> = {
  production: 'bg-vert-tint text-vert-deep',
  development: 'bg-bleu-ciel-tint text-bleu-ciel-deep',
};

export interface VersionPillProps {
  /** The `data-testid` prefix of the object: « critere » (`VersionedWording.entity`). */
  entity: string;
  tag: VersionTag;
  /** The number to name, or nothing when the surrounding line already names it. */
  version?: number;
  className?: string;
}

export function VersionPill({ entity, tag, version, className }: VersionPillProps) {
  const vitrine = useIsVitrine();
  if (vitrine) {
    if (tag === 'development' || version === undefined) return null;
    return (
      <span
        data-testid={`${entity}-version-jouee`}
        className={cn('font-semibold text-gray-900 text-sm leading-5', className)}
      >
        v{version}
      </span>
    );
  }
  return (
    <span
      data-testid={`${entity}-pastille-${tag}`}
      className={cn(
        'inline-flex items-center rounded-pill px-2.5 py-0.5 font-semibold text-xs leading-4',
        TAG_CLASS[tag],
        className,
      )}
    >
      {version === undefined ? VERSION_TAG_LABEL[tag] : `${VERSION_TAG_LABEL[tag]} v${version}`}
    </span>
  );
}
